import React, { useState, useEffect } from 'react';
import { useAccount } from 'wagmi';
import { AlertCircle } from 'lucide-react';
import { colors, spacing, typography } from '../../../tokens';
import { bsc } from 'wagmi/chains';
import { parseEther, type Address } from 'viem';

// Import atoms
import { Card, PageTitle, GradientText } from '../../../atoms';

// Import web3 molecules
import { ConnectButton } from '../../../molecules/web3/ConnectButton';
import { NetworkGuard } from '../../../molecules/web3/NetworkGuard';
import { useAuth } from '../../../web3/hooks/useAuth';

// Import hooks
import {
  useContractReads,
  useFormState,
  useStakingActions,
  usePreviewPosition,
} from './hooks';

// Import utils
import { calculateMultiplier } from './utils/formatters';

// Import components
import {
  GlobalStatistics,
  UserStatistics,
  StakingSteps,
  StakingForm,
  PositionsList,
  StreamSelection,
} from './components';

// Import config
import { getStakingAddresses, FAR_TOKEN, STAKING_CONTRACT } from './constants';

/**
 * Stakingpage - FAR Token Staking Interface
 *
 * User Flow:
 * 1. Connect wallet
 * 2. Sign message (SIWE authentication)
 * 3. Switch to BSC network
 * 4. View FAR balance and current stakes
 * 5. Stake tokens with lock period selection (5-180 days)
 * 6. View accumulated rewards
 * 7. Claim rewards
 * 8. Unstake tokens after lock period expires
 */
const Stakingpage: React.FC = () => {
  const { address, isConnected, chainId } = useAccount();
  const { isAuthenticated } = useAuth();

  // Dynamic chainId and contract addresses from backend config (supports dev/prod environments)
  const [requiredChainId, setRequiredChainId] = useState<number>(bsc.id);
  const [farToken, setFarToken] = useState<Address>(FAR_TOKEN);
  const [stakingContract, setStakingContract] = useState<Address>(STAKING_CONTRACT);

  // Load required chainId and contract addresses from backend config on mount
  useEffect(() => {
    getStakingAddresses().then((config) => {
      setRequiredChainId(config.CHAIN_ID);
      setFarToken(config.FAR_TOKEN);
      setStakingContract(config.STAKING_CONTRACT);
    });
  }, []);

  // Form state management
  const formState = useFormState();

  // Contract read operations
  const contractData = useContractReads(address, chainId, requiredChainId, farToken, stakingContract);

  // Staking actions
  const stakingActions = useStakingActions({
    farToken,
    stakingContract,
    setStakingStep: formState.setStakingStep,
    setErrorMessage: formState.setErrorMessage,
    setStakeAmount: formState.setStakeAmount,
    setSelectedStakeId: formState.setSelectedStakeId,
    refetchBalance: contractData.refetchBalance,
    refetchAllowance: contractData.refetchAllowance,
    refetchPositions: contractData.refetchPositions,
    refetchEnhancedStats: contractData.refetchEnhancedStats,
    refetchUserDashboard: contractData.refetchUserDashboard,
    refetchTotalPendingRewards: contractData.refetchTotalPendingRewards,
  });

  // Derived state
  const currentMultiplier = calculateMultiplier(formState.lockDays);
  const stakeAmountBigInt = formState.stakeAmount ? parseEther(formState.stakeAmount) : 0n;
  const needsApproval = stakeAmountBigInt > 0n && contractData.tokenAllowance !== undefined && contractData.tokenAllowance < stakeAmountBigInt;
  const isApproved = stakeAmountBigInt > 0n && contractData.tokenAllowance !== undefined && contractData.tokenAllowance >= stakeAmountBigInt;
  const currentStep = !isConnected ? 1 : chainId !== requiredChainId ? 2 : !isAuthenticated ? 3 : 4;

  // Preview position
  const { previewData } = usePreviewPosition(
    address,
    stakeAmountBigInt,
    formState.lockDays,
    chainId,
    requiredChainId,
    stakingContract,
    stakeAmountBigInt > 0n && isAuthenticated
  );

  return (
    <>
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .spin {
          animation: spin 1s linear infinite;
        }
      `}</style>

      <div style={{
        maxWidth: '1400px',
        margin: '0 auto',
        padding: spacing['2xl'],
      }}>
        <div style={{
          marginBottom: spacing['2xl'],
          textAlign: 'center',
        }}>
          <PageTitle style={{ marginBottom: spacing.md }}>
            <GradientText>FAR Token</GradientText> Staking
          </PageTitle>
          <p style={{
            fontSize: typography.fontSize.lg,
            color: colors.text.secondary.default,
          }}>
            Stake your FAR tokens and earn rewards with flexible lock periods
          </p>
        </div>

        {/* Progress Steps */}
        <StakingSteps currentStep={currentStep} />

        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: spacing['2xl'],
        }}>
          {/* Global Statistics */}
          <GlobalStatistics
            enhancedStats={contractData.enhancedStats}
            activeStakers={contractData.activeStakers}
          />

          {/* User Statistics - Show when connected and authenticated */}
          {isConnected && chainId === requiredChainId && isAuthenticated && (
            <UserStatistics
              farBalance={contractData.farBalance}
              totalPendingRewards={contractData.totalPendingRewards}
              userClaimedRewards={contractData.userClaimedRewards}
              userDashboard={contractData.userDashboard}
              enhancedStats={contractData.enhancedStats}
              onClaimRewards={() => stakingActions.handleClaim()}
              isClaimDisabled={
                !contractData.totalPendingRewards ||
                contractData.totalPendingRewards === 0n ||
                formState.stakingStep === 'claiming' ||
                stakingActions.isClaimPending ||
                stakingActions.isClaimLoading
              }
            />
          )}

          {/* Stream Selection - Show when connected and authenticated */}
          {isConnected && chainId === requiredChainId && isAuthenticated && (
            <StreamSelection />
          )}

          {/* Staking Card */}
          {currentStep === 1 && (
            <Card style={{ textAlign: 'center' }}>
              <AlertCircle size={48} color={colors.brand.cyan.base} style={{ marginBottom: spacing.md }} />
              <h3 style={{
                fontSize: typography.fontSize.xl,
                fontWeight: typography.fontWeight.semibold,
                color: colors.text.primary.default,
                margin: `0 0 ${spacing.sm} 0`,
              }}>
                Connect Your Wallet
              </h3>
              <p style={{
                fontSize: typography.fontSize.base,
                color: colors.text.secondary.default,
                marginBottom: spacing.md,
              }}>
                Please connect your wallet to start staking FAR tokens
              </p>
              <ConnectButton size="lg" />
            </Card>
          )}

          {currentStep === 2 && (
            <Card style={{ textAlign: 'center' }}>
              <NetworkGuard requiredChainId={requiredChainId} displayName="BSC Mainnet">
                <div />
              </NetworkGuard>
            </Card>
          )}

          {currentStep === 3 && (
            <Card style={{ textAlign: 'center' }}>
              <AlertCircle size={48} color={colors.brand.orange.base} style={{ marginBottom: spacing.md }} />
              <h3 style={{
                fontSize: typography.fontSize.xl,
                fontWeight: typography.fontWeight.semibold,
                color: colors.text.primary.default,
                margin: `0 0 ${spacing.sm} 0`,
              }}>
                Sign the Authentication Message
              </h3>
              <p style={{
                fontSize: typography.fontSize.base,
                color: colors.text.secondary.default,
                marginBottom: spacing.md,
              }}>
                Please sign the message in your wallet to verify your identity
              </p>
              <ConnectButton size="lg" requireAuth />
            </Card>
          )}

          {currentStep === 4 && (
            <StakingForm
                stakeAmount={formState.stakeAmount}
                lockDays={formState.lockDays}
                stakingStep={formState.stakingStep}
                errorMessage={formState.errorMessage}
                onStakeAmountChange={formState.setStakeAmount}
                onLockDaysChange={formState.setLockDays}
                onApprove={() => stakingActions.handleApprove(formState.stakeAmount)}
                onStake={() => stakingActions.handleStake(formState.stakeAmount, formState.lockDays)}
                farBalance={contractData.farBalance}
                lockConfig={contractData.lockConfig}
                needsApproval={needsApproval}
                isApproved={isApproved}
                previewData={previewData}
                currentMultiplier={currentMultiplier}
              />
            )}

          {/* Active Positions List */}
          {isConnected &&
            chainId === requiredChainId &&
            isAuthenticated &&
            contractData.userPositions &&
            Array.isArray(contractData.userPositions) &&
            contractData.userPositions.length > 0 && (
              <PositionsList
                positions={contractData.userPositions}
                totalPendingRewards={contractData.totalPendingRewards}
                stakingStep={formState.stakingStep}
                onExtendLock={(id, days) => stakingActions.handleExtendLock(id, days)}
                onWithdraw={(id, amount) => stakingActions.handleWithdraw(id, amount)}
                isExtendLockPending={stakingActions.isExtendLockPending}
                isExtendLockLoading={stakingActions.isExtendLockLoading}
                isWithdrawPending={stakingActions.isUnstakePending}
                isWithdrawLoading={stakingActions.isUnstakeLoading}
              />
            )}

          {/* Footer - Contract Information */}
          <div style={{
            textAlign: 'center',
            padding: spacing.xl,
            fontSize: typography.fontSize.sm,
            color: colors.text.muted.default,
          }}>
            <p style={{ margin: `0 0 ${spacing.xs} 0` }}>
              Powered by Farcana • Stake your FAR tokens and earn rewards
            </p>
            <p style={{ margin: `0 0 ${spacing.xs} 0`, fontSize: typography.fontSize.xs }}>
              Chain ID: {requiredChainId} • {requiredChainId === 11155111 ? 'Sepolia Testnet' : 'BSC Mainnet'}
            </p>
            <p style={{ margin: `0 0 ${spacing.xs} 0`, fontSize: typography.fontSize.xs }}>
              Staking Contract: {stakingContract.slice(0, 6)}...{stakingContract.slice(-4)}
            </p>
            <p style={{ margin: 0, fontSize: typography.fontSize.xs }}>
              FAR Token: {farToken.slice(0, 6)}...{farToken.slice(-4)}
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default Stakingpage;
